import { useState } from "react";
import type { MedicalCondition } from "../../types";
import { ApiError } from "../../types";
import { getApi } from "../../api";
import { notify } from "../../store/uiStore";
import { reportError } from "../../services/eventBus";
import { Alert } from "../Alert";
import { Button } from "../Button";
import { Card } from "../Card";
import { FieldRow } from "../FieldRow";
import { CheckboxInput, NumberInput, TextArea, TextInput } from "../fields";

interface NewConditionFormProps {
  /** Name typed into the search that matched nothing, so the actuary does not retype it. */
  initialName?: string;
  onCreated: (condition: MedicalCondition) => void;
  onOpenExisting: (conditionId: number) => void;
  onCancel: () => void;
  children?: never;
}

interface FormErrors {
  name?: string;
  ratingText?: string;
  debits?: string;
}

function duplicateIdFrom(error: unknown): number | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const body = error.body as { existingConditionId?: unknown } | undefined;
  return typeof body?.existingConditionId === "number" ? body.existingConditionId : null;
}

/**
 * Creates a condition together with its first rating in one step, so there is
 * never an empty condition to clean up. A duplicate name offers the existing one.
 */
export function NewConditionForm({ initialName = "", onCreated, onOpenExisting, onCancel }: NewConditionFormProps) {
  const [name, setName] = useState(initialName);
  const [conditionNotes, setConditionNotes] = useState("");
  const [ratingText, setRatingText] = useState("");
  const [time, setTime] = useState("");
  const [debits, setDebits] = useState<number | null>(null);
  const [isMedication, setIsMedication] = useState(false);
  const [ratingNotes, setRatingNotes] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [duplicate, setDuplicate] = useState<{ id: number; message: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleCreate = async () => {
    const nextErrors: FormErrors = {};
    if (name.trim() === "") nextErrors.name = "Condition name is required";
    if (ratingText.trim() === "") nextErrors.ratingText = "The first rating needs text";
    if (debits === null || !Number.isInteger(debits) || debits < 0) nextErrors.debits = "Enter whole-number debits";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || debits === null) return;
    setIsSaving(true);
    let created: MedicalCondition | null = null;
    try {
      created = await getApi().createCondition({ name: name.trim(), notes: conditionNotes.trim() || null });
      await getApi().createRating(created.id, { ratingText: ratingText.trim(), time: time.trim() || null, debits, notes: ratingNotes.trim() || null, isMedication });
      notify(`Created ${created.name} with its first rating. Skynet sees it immediately.`, "success");
      onCreated(created);
    } catch (error) {
      const existingId = duplicateIdFrom(error);
      if (existingId !== null && error instanceof ApiError) {
        setDuplicate({ id: existingId, message: error.message });
      } else {
        reportError(error, "NewConditionForm.create");
        if (created) {
          try {
            await getApi().deleteCondition(created.id);
          } catch (cleanupError) {
            reportError(cleanupError, "NewConditionForm.cleanup");
          }
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card
      title="New Condition"
      actions={
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
      }
    >
      <div className="space-y-4">
        <FieldRow>
          <TextInput
            id="new-condition-name"
            label="Condition name"
            value={name}
            onChange={(value) => {
              setName(value);
              setDuplicate(null);
            }}
            error={errors.name}
            required
          />
          <TextArea id="new-condition-notes" label="Condition notes" value={conditionNotes} onChange={setConditionNotes} rows={1} />
        </FieldRow>
        {duplicate && (
          <Alert
            type="warning"
            message={duplicate.message}
            actions={
              <Button variant="secondary" size="sm" onClick={() => onOpenExisting(duplicate.id)}>
                Open existing
              </Button>
            }
          />
        )}
        <fieldset className="rounded-lg border border-gray-200 p-4">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">First rating</legend>
          <div className="space-y-4">
            <FieldRow>
              <TextInput id="new-condition-rating-text" label="Rating" value={ratingText} onChange={setRatingText} error={errors.ratingText} required placeholder={isMedication ? "Drug name (Rx : is added)" : "e.g. Moderate, persistent"} />
              <TextInput id="new-condition-rating-time" label="Time" value={time} onChange={setTime} placeholder="e.g. within 2 yrs" />
            </FieldRow>
            <FieldRow>
              <NumberInput id="new-condition-rating-debits" label="Debits" value={debits} onChange={setDebits} error={errors.debits} required min={0} />
              <div className="flex items-end pb-2">
                <CheckboxInput id="new-condition-rating-is-medication" label="Medication (Rx)" checked={isMedication} onChange={setIsMedication} description="Medication ratings count as Rx debits." />
              </div>
            </FieldRow>
            <TextInput id="new-condition-rating-notes" label="Rating notes" value={ratingNotes} onChange={setRatingNotes} />
          </div>
        </fieldset>
        <div className="flex justify-end">
          <Button onClick={() => void handleCreate()} isLoading={isSaving}>
            Create condition
          </Button>
        </div>
      </div>
    </Card>
  );
}
